import { useState, useEffect } from 'react';
import { useAuth } from '../store/AuthContext';
import { useToast } from '../store/ToastContext';
import { useTheme } from '../store/ThemeContext';
import { linkWithEmail, signInWithEmail, logout, signUp } from '../services/auth';
import { getUserProfile, updateDisplayName, updateUserProfile } from '../services/users';

export const useSettings = () => {
    const { user } = useAuth();
    const { showToast } = useToast();
    const { theme, toggleTheme } = useTheme();

    // Profile State
    const [originalDisplayName, setOriginalDisplayName] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [isEditingName, setIsEditingName] = useState(false);

    // Payment State
    const [originalPaymentAppId, setOriginalPaymentAppId] = useState('');
    const [originalPaymentUsername, setOriginalPaymentUsername] = useState('');
    const [paymentAppId, setPaymentAppId] = useState('');
    const [paymentUsername, setPaymentUsername] = useState('');
    const [isEditingPayment, setIsEditingPayment] = useState(false);

    // Preferences State
    const [preferredCurrency, setPreferredCurrency] = useState(localStorage.getItem('preferred_currency') ?? 'USD');

    // Auth/Modal State
    const [isSecureModalOpen, setIsSecureModalOpen] = useState(false);
    const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
    const [loginModalMode, setLoginModalMode] = useState<'login' | 'signup'>('login');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [isLoadingProfile, setIsLoadingProfile] = useState(true);

    async function fetchProfile() {
        if (!user) return;
        setIsLoadingProfile(true);
        try {
            const profile = await getUserProfile(user.uid);
            if (profile) {
                setDisplayName(profile.displayName);
                setOriginalDisplayName(profile.displayName);
                if (profile.paymentConfig) {
                    setPaymentAppId(profile.paymentConfig.appId);
                    setOriginalPaymentAppId(profile.paymentConfig.appId);
                    setPaymentUsername(profile.paymentConfig.username);
                    setOriginalPaymentUsername(profile.paymentConfig.username);
                }
                if (profile.preferredCurrency) {
                    setPreferredCurrency(profile.preferredCurrency);
                    localStorage.setItem('preferred_currency', profile.preferredCurrency);
                }
            }
        } finally {
            setIsLoadingProfile(false);
        }
    }

    useEffect(() => {
        if (user) {
            queueMicrotask(() => { void fetchProfile(); });
        } else {
            queueMicrotask(() => setIsLoadingProfile(false));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    // Handlers
    const handleUpdateName = async () => {
        if (!user || !displayName) return;
        try {
            await updateDisplayName(user.uid, displayName);
            showToast("Name updated!", "success");
            setIsEditingName(false);
        } catch {
            showToast("Failed to update name", "error");
        }
    };

    const handleUpdatePayment = async () => {
        if (!user) return;
        try {
            await updateUserProfile(user.uid, {
                paymentConfig: paymentAppId && paymentUsername
                    ? { appId: paymentAppId, username: paymentUsername }
                    : null
            });
            showToast("Payment settings updated!", "success");
            setOriginalPaymentAppId(paymentAppId);
            setOriginalPaymentUsername(paymentUsername);
            setIsEditingPayment(false);
        } catch {
            showToast("Failed to update payment settings", "error");
        }
    };

    const handleCancelEditName = () => {
        setDisplayName(originalDisplayName);
        setIsEditingName(false);
    };

    const handleCancelEditPayment = () => {
        setPaymentAppId(originalPaymentAppId);
        setPaymentUsername(originalPaymentUsername);
        setIsEditingPayment(false);
    };

    const handleCurrencyChange = async (val: string) => {
        setPreferredCurrency(val);
        localStorage.setItem('preferred_currency', val);
        if (user) {
            try {
                await updateUserProfile(user.uid, { preferredCurrency: val });
            } catch (error) {
                console.error("Failed to sync currency preference", error);
            }
        }
    };

    const resetForm = () => {
        setEmail('');
        setPassword('');
    };

    const handleAuthError = (error: unknown) => {
        const err = error as { code?: string; message?: string };
        if (err.code === 'auth/email-already-in-use') {
            showToast("This email is already in use.", "error");
        } else if (err.code === 'auth/weak-password') {
            showToast("Password is too weak.", "error");
        } else if (err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
            showToast("Invalid email or password.", "error");
        } else {
            showToast("Error: " + (err.message ?? "Unknown error"), "error");
        }
    };

    const handleLinkEmail = async (e: React.SyntheticEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!email.trim() || !password.trim()) {
            showToast("Email and password are required", "error");
            return;
        }
        setIsSubmitting(true);
        try {
            await linkWithEmail(email, password);
            showToast("Account secured successfully!", "success");
            setIsSecureModalOpen(false);
            resetForm();
        } catch (error) {
            handleAuthError(error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleLogin = async (e: React.SyntheticEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!email.trim() || !password.trim()) {
            showToast("Email and password are required", "error");
            return;
        }
        setIsSubmitting(true);
        try {
            await signInWithEmail(email, password);
            showToast("Logged in successfully!", "success");
            setIsLoginModalOpen(false);
            resetForm();
        } catch (error) {
            handleAuthError(error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleLogout = async () => {
        try {
            await logout();
            showToast("Logged out successfully", "success");
        } catch {
            showToast("Error logging out", "error");
        }
    };

    const handleSignUp = async (e: React.SyntheticEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!email.trim() || !password.trim()) {
            showToast("Email and password are required", "error");
            return;
        }
        setIsSubmitting(true);
        try {
            await signUp(email, password);
            showToast("Account created successfully!", "success");
            setIsLoginModalOpen(false);
            resetForm();
        } catch (error) {
            handleAuthError(error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return {
        user,
        theme, toggleTheme,
        isLoadingProfile,

        // Profile
        displayName, setDisplayName,
        isEditingName, setIsEditingName,
        handleUpdateName, handleCancelEditName,

        // Payment
        paymentAppId, setPaymentAppId,
        paymentUsername, setPaymentUsername,
        isEditingPayment, setIsEditingPayment,
        handleUpdatePayment, handleCancelEditPayment,

        // Preferences
        preferredCurrency,
        handleCurrencyChange,

        // Auth Actions
        handleLogout,
        handleLinkEmail,
        handleLogin,
        handleSignUp,
        resetForm,

        // Modal State
        isSecureModalOpen, setIsSecureModalOpen,
        isLoginModalOpen, setIsLoginModalOpen,
        loginModalMode, setLoginModalMode,

        // Form State
        email, setEmail,
        password, setPassword,
        isSubmitting
    };
};
